import { useQuery } from "@apollo/client";
import { CNC_ACTIONS } from "./ActionsAPI";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import React, { useContext, useMemo } from "react";
import "./Actions.scss";
import {
  getFormattedDate,
  Tooltip,
  IconButton,
  TableCell,
  tableCellTypesEnum,
  tableFilterTypes,
  ModalContext,
  type ModalContextType,
} from "@SeemplicityDev/shared";
import EnableActionToggle from "./EnableActionToggle/EnableActionToggle";
import EditCron from "./EditCron/EditCron";
import LaunchAction from "./LaunchAction/LaunchAction";
import { TableCellProps, TableCellType } from "../../types";
import { TypedTable } from "../../types/table";

interface ActionDataProps {
  id: string;
  action_id: number;
  action_definition_name: string;
  subject_name: string;
  display_name: string;
  cron: string;
  next_run_time: string;
  enabled: boolean;
  params: string;
}

const Actions: React.FC<{ accountName: string; accountUuid: string }> = ({
  accountName,
  accountUuid,
}) => {
  const { openModal } = useContext(ModalContext) as ModalContextType;
  const TypedTableCell = TableCell as React.FC<TableCellProps>;

  const { data: actionsData } = useQuery(CNC_ACTIONS, {
    variables: {
      account_uuid: accountUuid,
    },
  });

  const columnHelper = createColumnHelper<ActionDataProps>();

  const columns: ColumnDef<ActionDataProps, any>[] = useMemo(
    () => [
      columnHelper.accessor("action_id", {
        id: "action_id",
        cell: (info) => <TableCell>{info.getValue()}</TableCell>,
        header: "ID",
        enableColumnFilter: false,
        size: 40,
        maxSize: 40,
      }),
      columnHelper.accessor("action_definition_name", {
        id: "action_definition_name",
        cell: (info) => (
          <TableCell>
            <Tooltip content={info.getValue()}>
              <span>{info.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        header: "Action Definition",
        filterFn: tableFilterTypes.SELECT.filterFn,
        meta: {
          filterType: tableFilterTypes.SELECT.filterType,
        },
      }),
      columnHelper.accessor("subject_name", {
        id: "subject_name",
        cell: (info) => (
          <TableCell>
            <Tooltip content={info.getValue()}>
              <span>{info.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),

        header: "Subject",
        enableColumnFilter: false,
      }),
      columnHelper.accessor("cron", {
        id: "cron",
        cell: (info) => (
          <TableCell>
            <EditCron
              accountUuid={accountUuid}
              actionId={info?.row?.original?.id}
              displayName={info?.row?.original?.display_name}
              cron={info.getValue()}
            ></EditCron>
          </TableCell>
        ),
        header: "Cron",
        enableColumnFilter: false,
        size: 100,
        maxSize: 100,
      }),
      columnHelper.accessor("next_run_time", {
        id: "next_run_time",
        cell: (info) => (
          <TableCell>{getFormattedDate(info.getValue())}</TableCell>
        ),
        header: "Next run",
        enableColumnFilter: false,
        size: 100,
        maxSize: 100,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <TypedTableCell type={tableCellTypesEnum.COMPONENT as TableCellType}>
            <div className={"action-actions"}>
              <EnableActionToggle
                accountUuid={accountUuid}
                actionId={info?.row?.original?.id}
                displayName={info?.row?.original?.display_name}
                enabled={info?.row?.original?.enabled}
              ></EnableActionToggle>
              <IconButton
                data-testid={`launch-action-${accountUuid}`}
                isIcon="seem-icon-launch"
                isRound
                withTooltip={{
                  content: "Launch Action",
                }}
                type={`button`}
                onClick={() => {
                  openModal({
                    title: `${accountName} - Launch "${info?.row?.original?.action_definition_name}" Action`,
                    size: "l",
                    overlay: true,
                    component: (
                      <LaunchAction
                        actionId={info?.row?.original?.id}
                        actionDisplayName={info?.row?.original?.display_name}
                        actionParams={info?.row?.original?.params}
                        accountName={accountName}
                        accountUuid={accountUuid}
                      />
                    ),
                  });
                }}
              />
            </div>
          </TypedTableCell>
        ),
        maxSize: 80,
        size: 80,
      }),
    ],
    [columnHelper]
  );

  return (
    <div className="cnc-actions-wrapper">
      <TypedTable
        noBorder
        size={"small"}
        columns={columns}
        data={actionsData?.cnc_actions}
        data-testid={"actions-list"}
        initialSortBy={[{ id: "next_run_time", desc: true }]}
        initialFilters={[{ id: "action_definition_name", value: "" }]}
      />
    </div>
  );
};

export default Actions;
